import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  FormControlLabel,
  Switch,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardHeader,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Button,
  Divider,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import api from '../services/api';
import WhatsAppChat from '../components/WhatsAppChat';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.label.whats-viral.me';

export default function AiAnnotationCheckPage() {
  const [activeTab, setActiveTab] = useState(0);

  // Annotators
  const [annotators, setAnnotators] = useState([]);
  const [loadingAnnotators, setLoadingAnnotators] = useState(false);

  // Selected annotator / submissions
  const [selectedUser, setSelectedUser] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [scawFilter, setScawFilter] = useState(false);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [selectedSubmissionIndex, setSelectedSubmissionIndex] = useState(null);

  // Chat + annotation
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [messages, setMessages] = useState([]);
  const [annotation, setAnnotation] = useState(null);
  const [loadingMessages, setLoadingMessages] = useState(false);

  // SCAW comparison tab
  const [scawMediaList, setScawMediaList] = useState([]);
  const [loadingScaw, setLoadingScaw] = useState(false);
  const scawLoadedRef = useRef(false);

  const [error, setError] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Load annotators on mount
  useEffect(() => {
    setLoadingAnnotators(true);
    api.get('/admin/ai/users')
      .then((res) => setAnnotators(res.data.users || []))
      .catch(() => setError('Failed to load annotators'))
      .finally(() => setLoadingAnnotators(false));
  }, []);

  const fetchSubmissions = useCallback(async (username, scaw) => {
    if (!username) return;
    setLoadingSubmissions(true);
    setSubmissions([]);
    setSelectedSubmissionIndex(null);
    setMessages([]);
    setAnnotation(null);
    setSelectedMedia(null);
    try {
      const res = await api.get(
        `/admin/ai/user/${encodeURIComponent(username)}/submissions${scaw ? '?scaw_only=true' : ''}`
      );
      setSubmissions(res.data.submissions || []);
    } catch {
      setError('Failed to load submissions');
    } finally {
      setLoadingSubmissions(false);
    }
  }, []);

  const handleAnnotatorClick = (username) => {
    setSelectedUser(username);
    setScawFilter(false);
    fetchSubmissions(username, false);
  };

  const handleScawToggle = (e) => {
    const val = e.target.checked;
    setScawFilter(val);
    fetchSubmissions(selectedUser, val);
  };

  const handleMediaSelect = useCallback(async (mediaFilename, index) => {
    setSelectedMedia(mediaFilename);
    setSelectedSubmissionIndex(index);
    setLoadingMessages(true);
    setMessages([]);
    setAnnotation(null);
    try {
      const [msgRes, annRes] = await Promise.all([
        api.get(`/admin/ai/chat/${encodeURIComponent(mediaFilename)}?username=${encodeURIComponent(selectedUser)}`),
        api.get(`/admin/ai/annotation/${encodeURIComponent(selectedUser)}/${encodeURIComponent(mediaFilename)}`),
      ]);
      setMessages(msgRes.data.messages || []);
      setAnnotation(annRes.data.annotation || null);
    } catch {
      setError('Failed to load chat or annotation');
    } finally {
      setLoadingMessages(false);
    }
  }, [selectedUser]);

  const handlePrev = () => {
    if (selectedSubmissionIndex === null || selectedSubmissionIndex <= 0) return;
    const newIndex = selectedSubmissionIndex - 1;
    handleMediaSelect(submissions[newIndex].media_filename, newIndex);
  };

  const handleNext = () => {
    if (selectedSubmissionIndex === null || selectedSubmissionIndex >= submissions.length - 1) return;
    const newIndex = selectedSubmissionIndex + 1;
    handleMediaSelect(submissions[newIndex].media_filename, newIndex);
  };

  const handleDeleteAnnotation = async () => {
    setDeleteConfirmOpen(false);
    try {
      await api.delete(`/admin/ai/annotation/${encodeURIComponent(selectedUser)}/${encodeURIComponent(selectedMedia)}`);
      const newSubmissions = submissions.filter((s) => s.media_filename !== selectedMedia);
      setSubmissions(newSubmissions);
      setAnnotation(null);
      setMessages([]);
      setSelectedMedia(null);
      if (newSubmissions.length > 0) {
        const nextIndex = Math.min(selectedSubmissionIndex || 0, newSubmissions.length - 1);
        handleMediaSelect(newSubmissions[nextIndex].media_filename, nextIndex);
      } else {
        setSelectedSubmissionIndex(null);
      }
    } catch (err) {
      setError('Failed to delete annotation');
    }
  };

  const handleTabChange = (_, newVal) => {
    setActiveTab(newVal);
    if (newVal === 1 && !scawLoadedRef.current) {
      scawLoadedRef.current = true;
      setLoadingScaw(true);
      api.get('/admin/ai/scaw-media')
        .then((res) => setScawMediaList(res.data.media || []))
        .catch(() => setError('Failed to load SCAW media'))
        .finally(() => setLoadingScaw(false));
    }
  };

  const handleScawLoadChat = (mediaFilename) => {
    setActiveTab(0);
    // We need a user selected — pick the first SCAW annotator for that media
    const mediaItem = scawMediaList.find((m) => m.media_filename === mediaFilename);
    const firstScawAnnotator = mediaItem?.annotators?.find((a) => a.contains_scaw);
    if (!firstScawAnnotator) return;
    const username = firstScawAnnotator.username;
    setSelectedUser(username);
    setScawFilter(false);
    setLoadingSubmissions(true);
    api.get(`/admin/ai/user/${encodeURIComponent(username)}/submissions`)
      .then((res) => {
        const subs = res.data.submissions || [];
        setSubmissions(subs);
        const idx = subs.findIndex((s) => s.media_filename === mediaFilename);
        handleMediaSelect(mediaFilename, idx >= 0 ? idx : null);
      })
      .catch(() => setError('Failed to load submissions for annotator'))
      .finally(() => setLoadingSubmissions(false));
  };

  return (
    <Box height="calc(100vh - 64px)" display="flex" flexDirection="column">
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Annotator Check" />
          <Tab label="SCAW Comparison" />
        </Tabs>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mx: 2, mt: 1 }}>
          {error}
        </Alert>
      )}

      {/* Tab 0 — Annotator Check */}
      {activeTab === 0 && (
        <Box display="flex" flex={1} overflow="hidden">
          {/* Left: Annotators */}
          <Box width={240} borderRight={1} borderColor="divider" overflow="auto" display="flex" flexDirection="column">
            <Typography variant="subtitle2" sx={{ p: 1, fontWeight: 700, bgcolor: 'grey.100' }}>
              Annotators
            </Typography>
            {loadingAnnotators ? (
              <Box display="flex" justifyContent="center" p={2}><CircularProgress size={24} /></Box>
            ) : (
              <List dense disablePadding>
                {annotators.map((ann) => (
                  <ListItemButton
                    key={ann.username}
                    selected={selectedUser === ann.username}
                    onClick={() => handleAnnotatorClick(ann.username)}
                  >
                    <ListItemText
                      primary={ann.username}
                      secondary={`${ann.submitted_annotation_count ?? 0} submitted · ${ann.contains_scaw_count ?? 0} SCAW`}
                      primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                      secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Box>

          {/* Middle: Submissions */}
          <Box width={320} borderRight={1} borderColor="divider" overflow="auto" display="flex" flexDirection="column">
            <Box sx={{ p: 1, bgcolor: 'grey.100', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" fontWeight={700} noWrap>
                {selectedUser || 'Select an annotator'}
              </Typography>
              {selectedUser && (
                <Box display="flex" alignItems="center" justifyContent="space-between" mt={0.5}>
                  <FormControlLabel
                    control={<Switch size="small" checked={scawFilter} onChange={handleScawToggle} />}
                    label={<Typography variant="caption">SCAW only</Typography>}
                    sx={{ m: 0 }}
                  />
                  <Box>
                    <IconButton size="small" onClick={handlePrev} disabled={!selectedSubmissionIndex}>
                      <ArrowBackIosNewIcon fontSize="inherit" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleNext}
                      disabled={selectedSubmissionIndex === null || selectedSubmissionIndex >= submissions.length - 1}
                    >
                      <ArrowForwardIosIcon fontSize="inherit" />
                    </IconButton>
                  </Box>
                </Box>
              )}
            </Box>
            {loadingSubmissions ? (
              <Box display="flex" justifyContent="center" p={2}><CircularProgress size={24} /></Box>
            ) : (
              <List dense disablePadding>
                {submissions.map((sub, idx) => (
                  <ListItemButton
                    key={sub.media_filename}
                    selected={selectedMedia === sub.media_filename}
                    onClick={() => handleMediaSelect(sub.media_filename, idx)}
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                            {sub.media_filename}
                          </Typography>
                          {sub.contains_scaw && (
                            <Chip label="SCAW" size="small" color="success" sx={{ height: 16, fontSize: 10 }} />
                          )}
                        </Box>
                      }
                      secondary={
                        sub.timestamp
                          ? new Date(
                              typeof sub.timestamp === 'number'
                                ? sub.timestamp * 1000
                                : sub.timestamp
                            ).toLocaleString()
                          : ''
                      }
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItemButton>
                ))}
                {!loadingSubmissions && submissions.length === 0 && selectedUser && (
                  <Typography variant="caption" color="text.secondary" sx={{ p: 1, display: 'block' }}>
                    No submissions found
                  </Typography>
                )}
              </List>
            )}
          </Box>

          {/* Right: Chat + Annotation */}
          <Box flex={1} display="flex" flexDirection="column" overflow="hidden">
            {/* Chat header */}
            <Box bgcolor="#075e54" px={2} py={1} flexShrink={0}>
              <Typography variant="subtitle2" color="white" noWrap>
                {selectedMedia || 'Select a media file'}
              </Typography>
            </Box>

            {/* Chat area */}
            <Box bgcolor="#e5ddd5" flex={1} overflow="auto" p={1} minHeight={0}>
              <WhatsAppChat
                messages={messages}
                isLoading={loadingMessages}
                apiBaseUrl={API_BASE_URL}
                token={null}
              />
            </Box>

            {/* Annotation panel */}
            {annotation && (
              <Box sx={{ flexShrink: 0, maxHeight: '35%', overflow: 'auto', borderTop: '1px solid', borderColor: 'divider' }}>
                <Card square elevation={0}>
                  <CardHeader
                    title="Annotation Details"
                    subheader={selectedUser}
                    sx={{ pb: 0 }}
                    titleTypographyProps={{ variant: 'subtitle2' }}
                    subheaderTypographyProps={{ variant: 'caption' }}
                    action={
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => setDeleteConfirmOpen(true)}
                      >
                        Delete
                      </Button>
                    }
                  />
                  <CardContent sx={{ pt: 1 }}>
                    <Alert
                      severity={annotation.contains_scaw ? 'success' : 'info'}
                      sx={{ mb: 1 }}
                    >
                      {annotation.contains_scaw ? 'Contains SCAW' : 'Does not contain SCAW'}
                    </Alert>
                    <Grid container spacing={1}>
                      {[
                        ['Quality', annotation.quality_rating],
                        ['Self AI', annotation.self_ai_likelihood],
                        ['Others AI', annotation.others_ai_likelihood],
                        ['Non-AI Feasibility', annotation.non_ai_feasibility],
                      ].map(([label, value]) => (
                        value != null && (
                          <Grid item key={label}>
                            <Chip
                              label={`${label}: ${value}`}
                              size="small"
                              variant="outlined"
                            />
                          </Grid>
                        )
                      ))}
                    </Grid>
                    {/* Content types */}
                    {annotation.content_types?.length > 0 && (
                      <Box mt={1}>
                        <Typography variant="caption" display="block" mb={0.5} fontWeight="bold">Content Types</Typography>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {annotation.content_types.map((ct) => (
                            <Chip key={ct} label={ct} size="small" color="primary" variant="outlined" />
                          ))}
                        </Box>
                        {/* Sub-tags for each content type */}
                        {annotation.conative_tags?.length > 0 && (
                          <Box mt={0.5}>
                            <Typography variant="caption" color="text.secondary">Conative: </Typography>
                            {annotation.conative_tags.map((t) => (
                              <Chip key={t} label={t} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                            ))}
                          </Box>
                        )}
                        {annotation.logistical_tags?.length > 0 && (
                          <Box mt={0.5}>
                            <Typography variant="caption" color="text.secondary">Logistical: </Typography>
                            {annotation.logistical_tags.map((t) => (
                              <Chip key={t} label={t} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                            ))}
                          </Box>
                        )}
                        {annotation.phatic_tags?.length > 0 && (
                          <Box mt={0.5}>
                            <Typography variant="caption" color="text.secondary">Phatic: </Typography>
                            {annotation.phatic_tags.map((t) => (
                              <Chip key={t} label={t} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}
                    {/* General message flags — shown for all annotations */}
                    <Box mt={1}>
                      <Divider sx={{ mb: 1 }} />
                      <Typography variant="caption" display="block" mb={0.5} fontWeight="bold">General Flags</Typography>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {[
                          ['Misinformation', annotation.misinformation],
                          ['Hateful', annotation.hateful],
                          ['Political', annotation.political],
                          ['Partisan', annotation.partisan],
                          ['Religious', annotation.religious],
                          ['Caste', annotation.caste],
                          ['Socially Relevant', annotation.socially_relevant],
                          ['Can Theoretically Verify', annotation.can_theoretically_verify],
                          ['Found Evidence', annotation.found_evidence],
                          ['Is Claim Misleading', annotation.is_claim_misleading],
                          ['People React in Group', annotation.people_react_in_group],
                          ['Also Expressive in SCAW', annotation.also_expressive_in_scaw],
                          ['Is Maybe Closer To', annotation.is_maybe_closer_to],
                        ].filter(([, v]) => v != null).map(([label, value]) => (
                          <Chip
                            key={`gen-${label}`}
                            label={`${label}: ${value === true ? 'Yes' : value === false ? 'No' : value}`}
                            size="small"
                            color={value === true ? 'error' : 'default'}
                            variant="outlined"
                          />
                        ))}
                        {annotation.partisan_congruent && (
                          <Chip label={`Partisan Congruent: ${annotation.partisan_congruent}`} size="small" variant="outlined" />
                        )}
                        {annotation.reaction_nature && (
                          <Chip label={`Reaction Nature: ${annotation.reaction_nature}`} size="small" variant="outlined" />
                        )}
                      </Box>
                      {annotation.evidence_text && (
                        <Typography variant="caption" display="block" mt={0.5}>
                          <strong>Evidence:</strong> {annotation.evidence_text}
                        </Typography>
                      )}
                      {annotation.misleading_explanation && (
                        <Typography variant="caption" display="block" mt={0.5}>
                          <strong>Misleading explanation:</strong> {annotation.misleading_explanation}
                        </Typography>
                      )}
                      {annotation.scaw_expressive_claim && (
                        <Typography variant="caption" display="block" mt={0.5}>
                          <strong>SCAW expressive claim:</strong> {annotation.scaw_expressive_claim}
                        </Typography>
                      )}
                      {annotation.reaction_emotions?.length > 0 && (
                        <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                          <Typography variant="caption" color="text.secondary">Reaction emotions: </Typography>
                          {annotation.reaction_emotions.map((e) => (
                            <Chip key={e} label={e} size="small" />
                          ))}
                        </Box>
                      )}
                    </Box>
                    {annotation.contains_scaw && (
                      <Box mt={1}>
                        {/* SCAW mode */}
                        {annotation.scaw_mode_choice && (
                          <Typography variant="caption" display="block" mb={0.5}>
                            <strong>Mode:</strong> {annotation.scaw_mode_choice}
                          </Typography>
                        )}
                        {/* Claim description */}
                        {annotation.claim_description && (
                          <Typography variant="caption" display="block" mb={0.5}>
                            <strong>Claim:</strong> {annotation.claim_description}
                          </Typography>
                        )}
                        {annotation.claim_info && !annotation.claim_description && (
                          <Typography variant="caption" display="block" mb={0.5}>
                            <strong>Claim info:</strong> {typeof annotation.claim_info === 'object' ? JSON.stringify(annotation.claim_info) : annotation.claim_info}
                          </Typography>
                        )}
                        {/* Misinformation part */}
                        {annotation.misinformation_part && (
                          <Typography variant="caption" display="block" mb={0.5}>
                            <strong>Misinfo part:</strong> {annotation.misinformation_part}
                          </Typography>
                        )}
                        {/* SCAW tags (from tags object) */}
                        {annotation.tags && (
                          <>
                            <Typography variant="caption" display="block" mt={1} mb={0.5} fontWeight="bold">SCAW Tags</Typography>
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                              {[
                                ['Hateful Content', annotation.tags.hateful_content],
                                ['Political', annotation.tags.political],
                                ['Partisan', annotation.tags.partisan],
                                ['Religious', annotation.tags.religious],
                                ['Religious Iconography', annotation.tags.religious_iconography],
                                ['Caste', annotation.tags.caste],
                              ].filter(([, v]) => v != null).map(([label, value]) => (
                                <Chip
                                  key={`scaw-${label}`}
                                  label={`${label}: ${value === true ? 'Yes' : value === false ? 'No' : value}`}
                                  size="small"
                                  color={value === true ? 'error' : 'default'}
                                  variant="outlined"
                                />
                              ))}
                              {annotation.tags.propaganda_for && (
                                <Chip label={`Propaganda for: ${annotation.tags.propaganda_for}`} size="small" color="warning" variant="outlined" />
                              )}
                              {annotation.tags.propaganda_against && (
                                <Chip label={`Propaganda against: ${annotation.tags.propaganda_against}`} size="small" color="warning" variant="outlined" />
                              )}
                              {annotation.tags.event_scope && (
                                <Chip label={`Scope: ${annotation.tags.event_scope}`} size="small" variant="outlined" />
                              )}
                            </Box>
                            {annotation.tags.hateful_content_description && (
                              <Typography variant="caption" display="block" mt={0.5}>
                                <strong>Hateful content desc:</strong> {annotation.tags.hateful_content_description}
                              </Typography>
                            )}
                            {annotation.tags.common_theories?.length > 0 && (
                              <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                                {annotation.tags.common_theories.map((t) => (
                                  <Chip key={t} label={t} size="small" color="warning" />
                                ))}
                              </Box>
                            )}
                          </>
                        )}
                        {/* SCAW tags (legacy array) */}
                        {annotation.scaw_tags && (
                          <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                            {(Array.isArray(annotation.scaw_tags) ? annotation.scaw_tags : [annotation.scaw_tags]).map((tag) => (
                              <Chip key={tag} label={tag} size="small" color="warning" />
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Annotation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this annotation by <strong>{selectedUser}</strong> for <strong>{selectedMedia}</strong>? This will also allow the user to re-annotate this media.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteAnnotation} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Tab 1 — SCAW Comparison */}
      {activeTab === 1 && (
        <Box flex={1} overflow="auto" p={2}>
          {loadingScaw ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Media Filename</TableCell>
                    <TableCell align="center">SCAW</TableCell>
                    <TableCell align="center">Non-SCAW</TableCell>
                    <TableCell align="center">Total</TableCell>
                    <TableCell>Annotators</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scawMediaList.map((item) => (
                    <TableRow key={item.media_filename}>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {item.media_filename}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{item.scaw_count}</TableCell>
                      <TableCell align="center">{item.non_scaw_count}</TableCell>
                      <TableCell align="center">{item.total_annotations}</TableCell>
                      <TableCell>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {(item.annotators || []).map((ann) => (
                            <Chip
                              key={ann.username}
                              label={ann.username}
                              size="small"
                              color={ann.contains_scaw ? 'success' : 'default'}
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleScawLoadChat(item.media_filename)}
                        >
                          Load Chat
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {scawMediaList.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary">No SCAW media found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
    </Box>
  );
}
